import { useMemo } from "react";
import {
  Activity,
  Compass,
  Flame,
  MapPin,
  Radar,
  Snowflake,
  Sun,
  Target,
  TrendingUp,
} from "lucide-react";

// Sports-analytics-meets-CRM colour system. All indicators pull from this so
// the "traffic-light" reads consistently across cards and stat tiles.
const STATUS_TIERS = {
  gridReady: {
    key: "gridReady",
    label: "Grid-Ready",
    accent: "#10B981",
    range: [8, 10],
    icon: Flame,
  },
  observation: {
    key: "observation",
    label: "Under Observation",
    accent: "#F59E0B",
    range: [5, 7],
    icon: Radar,
  },
  cold: {
    key: "cold",
    label: "Cold Territory",
    accent: "#EF4444",
    range: [1, 4],
    icon: Snowflake,
  },
};

function tierForScore(score) {
  if (score >= 8) return STATUS_TIERS.gridReady;
  if (score >= 5) return STATUS_TIERS.observation;
  return STATUS_TIERS.cold;
}

// Mock scout batches — reads exactly the way a scouting network would file
// weekend intel: region, sector, headcount, score, disposition.
const SCOUT_REPORTS = [
  {
    id: "scout-01",
    region: "Marion, OH",
    sector: "Auto & Mobile Mechanics",
    prospects: 24,
    score: 9,
    status: "Ready to contact",
    filed: "This morning · 07:12 AM",
    scout: "Field Team · North",
  },
  {
    id: "scout-02",
    region: "Delaware, OH",
    sector: "Trades — HVAC / Plumbing",
    prospects: 18,
    score: 8,
    status: "3 warm intros lined up",
    filed: "Yesterday · 18:44",
    scout: "Field Team · North",
  },
  {
    id: "scout-03",
    region: "Caledonia, OH",
    sector: "Campground & Family Rec",
    prospects: 6,
    score: 10,
    status: "River Bend pilot live",
    filed: "This week · Monday",
    scout: "Alpha · Direct",
  },
  {
    id: "scout-04",
    region: "Ashley, OH",
    sector: "Restaurants & Hospitality",
    prospects: 11,
    score: 6,
    status: "Sampling weekend traffic",
    filed: "This week · Tuesday",
    scout: "Field Team · South",
  },
  {
    id: "scout-05",
    region: "Bucyrus, OH",
    sector: "Professional Services",
    prospects: 9,
    score: 7,
    status: "First-touch scheduled Thu",
    filed: "This week · Wednesday",
    scout: "Field Team · North",
  },
  {
    id: "scout-06",
    region: "Mansfield, OH",
    sector: "Powersports / MC",
    prospects: 14,
    score: 8,
    status: "Bike-night walk-thru queued",
    filed: "Yesterday · 09:20",
    scout: "Field Team · North",
  },
  {
    id: "scout-07",
    region: "Kenton, OH",
    sector: "General Contractors",
    prospects: 5,
    score: 3,
    status: "No signal — deprioritized",
    filed: "This week · Monday",
    scout: "Field Team · South",
  },
  {
    id: "scout-08",
    region: "Columbus, OH · North Ring",
    sector: "Legal & Advisory",
    prospects: 16,
    score: 7,
    status: "Referral network warming",
    filed: "This morning · 08:41 AM",
    scout: "Alpha · Direct",
  },
];

export default function ScoutReports() {
  const summary = useMemo(() => {
    const total = SCOUT_REPORTS.length;
    const prospects = SCOUT_REPORTS.reduce((s, r) => s + r.prospects, 0);
    const ready = SCOUT_REPORTS.filter((r) => r.score >= 8).length;
    // Conversion-rate demo math: (Grid-Ready / Total) as a proxy readiness pct.
    const conversion = total ? Math.round((ready / total) * 100) : 0;
    return { total, prospects, ready, conversion };
  }, []);

  return (
    <div className="space-y-8" data-testid="scout-reports">
      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="scout-summary">
        <StatTile
          label="Territories Scouted"
          value={summary.total}
          accent="#94A3B8"
          icon={<Compass size={14} />}
          testId="scout-stat-territories"
        />
        <StatTile
          label="Prospects in Pipeline"
          value={summary.prospects}
          accent="#60A5FA"
          icon={<Activity size={14} />}
          testId="scout-stat-prospects"
        />
        <StatTile
          label="Grid-Ready Targets"
          value={summary.ready}
          accent="#10B981"
          icon={<Flame size={14} />}
          testId="scout-stat-ready"
        />
        <StatTile
          label="Conversion Rate"
          value={`${summary.conversion}%`}
          accent="#D4AF37"
          icon={<TrendingUp size={14} />}
          testId="scout-stat-conversion"
        />
      </div>

      {/* Section header */}
      <div className="flex items-center gap-3">
        <span
          className="h-px flex-1 max-w-[64px]"
          style={{ background: "linear-gradient(90deg, rgba(16,185,129,0.55), transparent)" }}
        />
        <div className="text-[10px] uppercase tracking-[0.34em] text-slate-400">
          Latest Reports
        </div>
        <span
          className="h-px flex-1"
          style={{ background: "linear-gradient(90deg, transparent, rgba(16,185,129,0.22))" }}
        />
      </div>

      {/* Report cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="scout-report-grid">
        {SCOUT_REPORTS.map((r) => (
          <ScoutCard key={r.id} report={r} />
        ))}
      </div>
    </div>
  );
}

function ScoutCard({ report }) {
  const tier = tierForScore(report.score);
  const Icon = tier.icon;
  return (
    <div
      className="rounded-sm border p-4 relative"
      style={{
        borderColor: `${tier.accent}44`,
        background:
          `linear-gradient(180deg, ${tier.accent}0C 0%, rgba(6,11,20,0.7) 100%)`,
        boxShadow: `0 0 32px -20px ${tier.accent}66`,
      }}
      data-testid={`scout-card-${report.id}`}
    >
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${tier.accent}88, transparent)` }}
      />

      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-1">
            <MapPin size={11} />
            {report.region}
          </div>
          <div className="font-display text-lg text-white leading-tight">
            {report.sector}
          </div>
        </div>
        <div
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[9px] uppercase tracking-[0.22em]"
          style={{
            background: `${tier.accent}18`,
            border: `1px solid ${tier.accent}55`,
            color: tier.accent,
          }}
          data-testid={`scout-tier-${report.id}`}
        >
          <Icon size={10} /> {tier.label}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[9px] uppercase tracking-[0.28em] text-slate-500 mb-0.5">
            Prospects Identified
          </div>
          <div className="text-xl font-display text-white leading-tight">
            {report.prospects}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.28em] text-slate-500 mb-0.5">
            Grid-Ready Score
          </div>
          <div className="flex items-baseline gap-1">
            <div
              className="text-xl font-display leading-tight"
              style={{ color: tier.accent }}
            >
              {report.score}
            </div>
            <div className="text-xs text-slate-500">/ 10</div>
          </div>
        </div>
      </div>

      {/* Score bar */}
      <div className="h-1.5 rounded-full overflow-hidden bg-white/5 mb-3">
        <div
          className="h-full rounded-full"
          style={{
            width: `${report.score * 10}%`,
            background: `linear-gradient(90deg, ${tier.accent}55, ${tier.accent})`,
          }}
        />
      </div>

      <div className="flex items-start justify-between gap-3 text-[11px]">
        <div className="flex items-center gap-1.5" style={{ color: tier.accent }}>
          <Target size={11} />
          <span className="font-mono-aviation">{report.status}</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 text-right">
          {report.filed}
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-white/5 text-[9px] uppercase tracking-[0.28em] text-slate-500 flex items-center gap-1.5">
        <Sun size={10} /> Scout · {report.scout}
      </div>
    </div>
  );
}

function StatTile({ label, value, accent, icon, testId }) {
  return (
    <div
      className="rounded-sm border p-4"
      style={{
        borderColor: `${accent}33`,
        background: "rgba(6,11,20,0.6)",
      }}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] text-slate-500 mb-2">
        <span style={{ color: accent }}>{icon}</span>
        {label}
      </div>
      <div
        className="font-display text-3xl leading-none"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}
